import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { GradientOrbs } from './GradientOrbs';
import { MagneticButton } from '@/components/ui/magnetic-button';
import { ParticleBackground } from './ParticleBackground';
import { FloatingElements } from './FloatingElements';
import { AnimatedBlob } from './AnimatedBlob';
import { FlowingWorkflow } from './FlowingWorkflow';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  ArrowRight,
  Lock,
  CreditCard,
  Rocket,
} from 'lucide-react';

export function HeroSection() {
  const { t } = useTranslation();

  const trustBadges = [
    { icon: Rocket, text: t('landing.hero.trustBadge1') },
    { icon: Lock, text: t('landing.hero.trustBadge2') },
    { icon: CreditCard, text: t('landing.hero.trustBadge3') },
  ];

  return (
    <section className="relative overflow-hidden min-h-screen flex items-center">
      {/* Background Animations */}
      <ParticleBackground />
      <FloatingElements />
      <AnimatedBlob />

      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 via-transparent to-teal-50/50 dark:from-emerald-950/20 dark:to-teal-950/20" />

      {/* OpenClaw Logo Background */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.5, delay: 0.3 }}
      >
        <motion.img
          src="/openclaw.svg"
          alt=""
          className="w-[60%] md:w-[50%] lg:w-[40%] h-auto opacity-[0.03] dark:opacity-[0.05]"
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
        />
      </motion.div>

      <GradientOrbs />
      <div className="container mx-auto px-4 py-8 md:py-12 relative w-full">
        <div className="max-w-7xl mx-auto">
          {/* Hero Content - Centered */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="text-center mb-8"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <motion.div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-semibold shadow-lg shadow-emerald-500/30"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Sparkles className="h-3 w-3" />
                🚀 {t('landing.hero.trustBadge1')}
              </motion.div>
            </div>
            <motion.h1
              className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mb-3 leading-tight max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              {t('landing.hero.title')}{' '}
              <span className="bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
                {t('landing.hero.titleHighlight')}
              </span>
            </motion.h1>
            <motion.p
              className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-6 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
            >
              {t('landing.hero.subtitle')}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
            >
              <MagneticButton strength={12}>
                <Link to="/register">
                  <Button size="lg" className="gap-2 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all hover:scale-105">
                    <Rocket className="h-4 w-4" />
                    {t('landing.hero.cta')}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </MagneticButton>
              <MagneticButton strength={12}>
                <a href="#features">
                  <Button variant="outline" size="lg" className="gap-2">
                    <Sparkles className="h-4 w-4" />
                    {t('landing.hero.ctaSecondary')}
                  </Button>
                </a>
              </MagneticButton>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              className="flex flex-wrap items-center justify-center gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.8 }}
            >
              {trustBadges.map((badge, i) => (
                <motion.div
                  key={i}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/5 border border-emerald-500/20 text-xs font-medium hover:bg-emerald-500/10 transition-colors cursor-default"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <badge.icon className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-foreground">{badge.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Flowing Workflow Visualization */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <FlowingWorkflow />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
